export const namespaced = true;

import BookService from '@/services/BookService.js';
import TrialCourseService from '@/services/TrialCourseService.js';
import cloneDeep from 'lodash/cloneDeep';
import omit from 'lodash/omit';
import sortBy from 'lodash/sortBy';

export const state = {
  trialCourses: [{ id: 0, book: null, bookPart: null, bookChapter: null }],
  trialCourseList: [],
  books: [],
};

export const mutations = {
  SET_TRIAL_COURSE_AT_INDEX(state, payload) {
    state.trialCourses.splice(payload.index, 1, payload.trialCourse);
  },
  ADD_TRIAL_COURSE(state, range) {
    state.trialCourses.push(range);
  },
  DELETE_TRIAL_COURSE_AT_INDEX(state, index) {
    state.trialCourses.splice(index, 1);
  },
  SET_BOOKS(state, books) {
    state.books = books;
  },
  CREATE_TRIAL_COURSES() {
    // We just call API to create Trial Course here, we don't need to save anything
  },
  CLEAR_TRIAL_COURSES(state) {
    state.trialCourses = [{ id: 0, book: null, bookPart: null, bookChapter: null }];
  },
  SET_TRIAL_COURSE_LIST(state, trialCourseList) {
    state.trialCourseList = trialCourseList;
  },
  DELETE_TRIAL_COURSE_FROM_DB() {
    // We just call API to delete Trial Course here, we don't need to save anything
  },
};

export const actions = {
  updateTrialCourse({ commit, state }, trialCourse) {
    if (trialCourse) {
      const payload = {
        index: state.trialCourses.findIndex((item) => item.id === trialCourse.id),
        trialCourse: trialCourse,
      };
      commit('SET_TRIAL_COURSE_AT_INDEX', payload);
    } else {
      commit('ADD_TRIAL_COURSE', trialCourse);
    }
  },
  addTrialCourse({ commit }, range) {
    commit('ADD_TRIAL_COURSE', range);
  },
  deleteTrialCourse({ commit, state }, id) {
    const index = state.trialCourses.findIndex((item) => item.id == id);
    commit('DELETE_TRIAL_COURSE_AT_INDEX', index);
  },
  fetchBooks({ commit }) {
    return BookService.all({ type: 'extended' }).then((response) => {
      commit('SET_BOOKS', response.data);
    });
  },
  createTrialCourses({ commit, state }) {
    let formattedTrialCourses = state.trialCourses.map((trialCourse) => {
      let formattedTrialCourse = cloneDeep(trialCourse);
      if (formattedTrialCourse.book) {
        formattedTrialCourse.bookId = formattedTrialCourse.book.id;
        formattedTrialCourse = omit(formattedTrialCourse, ['book']);
      }
      if (formattedTrialCourse.bookPart) {
        formattedTrialCourse.bookPartId = formattedTrialCourse.bookPart.id;
        formattedTrialCourse = omit(formattedTrialCourse, ['bookPart']);
      }
      if (formattedTrialCourse.bookChapter) {
        formattedTrialCourse.bookChapterId = formattedTrialCourse.bookChapter.id;
        formattedTrialCourse = omit(formattedTrialCourse, ['bookChapter']);
      }
      formattedTrialCourse = omit(formattedTrialCourse, ['id']);
      return formattedTrialCourse;
    });
    return TrialCourseService.create({ data: formattedTrialCourses }).then((response) => {
      commit('CREATE_TRIAL_COURSES');
      commit('CLEAR_TRIAL_COURSES');
      return response.data.code;
    });
  },
  fetchTrialCourses({ commit }) {
    return TrialCourseService.all().then((response) => {
      commit('SET_TRIAL_COURSE_LIST', response.data);
    });
  },
  deleteTrialCourseFromDB({ commit }, trialCourseId) {
    return TrialCourseService.delete(trialCourseId)
      .then(() => {
        commit('DELETE_TRIAL_COURSE_FROM_DB');
      })
      .catch((err) => {
        throw err;
      });
  },
};

export const getters = {
  getBookPartsFromTrialCourse: (state) => (trialCourse) => {
    if (trialCourse && trialCourse.book) {
      const book = state.books.find((item) => item.id == trialCourse.book.id);
      if (book) {
        const sortedByBookparts = sortBy(book.bookParts, 'code');
        return sortedByBookparts;
      }
    }
    return [];
  },
  getBookChaptersFromTrialCourse: (state) => (trialCourse) => {
    if (trialCourse && trialCourse.book && trialCourse.bookPart) {
      const book = state.books.find((item) => item.id == trialCourse.book.id);
      if (book) {
        const bookPart = book.bookParts.find((item) => item.id == trialCourse.bookPart.id);
        if (bookPart) {
          const sortedByBookChapters = sortBy(bookPart.bookChapters, 'code');
          return sortedByBookChapters;
        }
      }
    }
    return [];
  },
  listOfBooksForCustomTrialCourse: (state) => {
    // List of book code not selectable for a custom trial course
    const codeListNoIncluded = [
      '11980',
      '11990',
      '51910',
      '51920',
      '51930',
      '51940',
      '51912',
      '51922',
      '51932',
      '51942',
    ];
    return state.books.filter((currentBook) => {
      return codeListNoIncluded.indexOf(currentBook.code) == -1;
    });
  },
};
